import React, { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';
import { Form, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import axios from 'axios';
import Swal from 'sweetalert2';
import Cookies from 'js-cookie';

import { Translate } from '../../../../helpers/Translate';
import { url } from '../../../../store/initialStatus';
import PanelHeader from '../PanelHeader/PanelHeader';
import MedicalRecord from './MedicalRecord';
// import Chromebook from './ChromeBook';
import EnrollmentCharter from './EnrollmentCharter';

import "./Home.css";
import img12 from "./assets/img/menu.png";
import img2 from "./assets/img/ivy_transportation.png";
import finance from "./assets/img/finance.jpg";

const t = new Translate();

/** A quick-access tile: internal route (`to`) or external link (`href`). */
interface HomeTileProps {
  title: string;
  image: string;
  to?: string;
  href?: string;
}

/** Renders one quick-access tile. External links open in a new tab. */
const HomeTile = ({ title, image, to, href }: HomeTileProps) => {
  const content = (
    <>
      <div className="pp-home-tile-media">
        <img src={image} alt="" />
      </div>
      <h2 className="pp-home-tile-title">
        {t.translate(title)}
        {href && (
          <i className="pp-home-tile-external fas fa-external-link-alt" aria-hidden="true"></i>
        )}
      </h2>
    </>
  );

  if (href) {
    return (
      <a className="pp-home-tile" href={href} target="_blank" rel="noreferrer">
        {content}
      </a>
    );
  }
  return (
    <NavLink className="pp-home-tile" to={to ?? ""}>
      {content}
    </NavLink>
  );
};

/**
 * Dashboard landing page.
 *
 * - Shows quick-access tiles (LMS, Cafeteria, Transportation, Smart Wallet).
 * - Warns the guardian when their account is on hold (overdue installment).
 * - Opens the "required documents" modal when documents are missing.
 * - Mounts the mandatory medical record and enrollment charter modals.
 */
const Home = () => {
  const navigate = useNavigate();
  const transjwt: any = useSelector((state: any) => state.Parent.transjwt);
  const Parent: any = useSelector((state: any) => state.Parent);

  const [lms_token, setLmsToken] = useState(Cookies.get("lms_jwt"));

  const [studentsRequiredDocs, setStudentsRequiredDocs] = useState([]);
  const [requiredDocsModal, setRequiredDocsModal] = useState(false);

  const [commenRequiredDocs, setCommenRequiredDocs] = useState([]);
  const [uploadedDocs, setUploadedDocs] = useState<any>([]);

  // Account on hold: prompt the guardian to pay the overdue installment.
  useEffect(() => {
    if (Parent.guardian?.guardian_id != undefined) {
      axios.get(`${url}auth/checkifhold.php?guardian_id=${Parent.guardian?.guardian_id}`).then((res) => {
        const data = res.data.data;
        const isHeld =
          typeof data === "object" && data !== null
            ? data.ishold == 1 || data.ishold == 2 || (data.held_students && data.held_students.length > 0)
            : data == 1 || data == 2;

        if (isHeld) {
          const heldStudents: string[] =
            typeof data === "object" && Array.isArray(data?.held_students) ? data.held_students : [];

          Swal.fire({
            title:
              t.translate("You have an overdue installment that needs to be paid.") +
              (heldStudents.length > 0 ? ` (${heldStudents.join(", ")})` : ""),
            html:
              heldStudents.length > 0
                ? `${t.translate("Please note that services and reports for")} <strong>${heldStudents.join(
                    ", ",
                  )}</strong> ${t.translate("will remain on hold until overdue installments are paid.")}`
                : t.translate("Please note that your account will remain on hold until all overdue installments are paid"),
            showCancelButton: true,
            confirmButtonText: t.translate("Click here to pay"),
            icon: "warning",
          }).then((alertRes) => {
            if (alertRes.isConfirmed) {
              navigate({ pathname: "/dashboard/finance/schoolfees" });
            }
          });
        }
      });
    }
  }, [Parent.guardian?.guardian_id]);

  // Missing documents: open the upload modal and collect the documents that
  // are shared by all students ("common" files) so they are asked for once.
  useEffect(() => {
    if (Parent.guardian.guardian_id != undefined) {
      axios.get(`${url}guardian/checkrequireddocs.php?guardian_id=${Parent.guardian.guardian_id}`).then((res) => {
        if (res.data.data?.length > 0) {
          try {
            setRequiredDocsModal(true);
            setStudentsRequiredDocs(res.data.data);
            let tempFiles: any = [];
            res.data.data?.forEach((studentsFiles: any) => {
              studentsFiles?.required_docs?.forEach((file: any) => {
                let foundIndex = tempFiles?.findIndex((f: any) => f.id == file.id);
                if (foundIndex == -1) {
                  if (file.commenfile == 1)
                    tempFiles.push(file);
                }
              });
            });
            setCommenRequiredDocs(tempFiles);
          } catch (error) {

          }
        }
      });
    }
  }, [Parent.guardian.guardian_id]);

  // LMS single sign-on token, cached in a cookie for 3 days.
  useEffect(() => {
    if (lms_token == undefined)
      axios.get(`${url}guardian/getlmstoken.php`).then((res) => {
        Cookies.set("lms_jwt", res.data.data, { sameSite: 'none', secure: true, expires: 3 });
        setLmsToken(res.data.data);
      });
  }, [lms_token]);

  const handleSubmit = (e: any) => {
    e.preventDefault();
    Swal.showLoading();
    const blogData = new FormData();

    uploadedDocs.forEach((doc: any) => {
      blogData.append(`requireddocument_${doc.id}`, doc.uploadedFile);
      blogData.append(`parent_id`, Parent.guardian.guardian_id);
    });

    axios.post(`${url}guardian/uploadrequireddocs.php`, blogData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }).then((res: any) => {
      Swal.fire({
        title: t.translate("Documents uploaded successfully"),
        icon: "success",
      });
      setRequiredDocsModal(false);
    });
  };

  return (
    <Fragment>
      <MedicalRecord />

      {/* Legacy Chromebook acknowledgment retained for possible restoration. */}
      {/* <Chromebook /> */}
      <EnrollmentCharter />
      <PanelHeader size="sm" />

      {/* Quick-access tiles */}
      <div className="content">
        <div className="pp-home-tiles">
          <HomeTile
            title="IVY's LMS"
            image="https://staff.ivyis.org/images/ivylms.jpg"
            href={`${process.env.REACT_APP_LMS_BASURL}include/store_parent_token.php?token=${lms_token}&emaildomain=ivyis.org`}
          />
          <HomeTile title="Cafeteria" image={img12} to="/dashboard/cafeteria" />
          <HomeTile
            title="Transportation"
            image={img2}
            href={`https://transportation.ivyis.org/?hash=` + transjwt}
          />
          <HomeTile title="Smart Wallet" image={finance} to="/dashboard/finance" />
        </div>
      </div>

      {/* Required documents modal */}
      <Modal className="sizeModal pp-form-modal" isOpen={requiredDocsModal} scrollable>
        <ModalHeader>{t.translate("Please upload required documents")}</ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSubmit}>
            {/* Documents shared by all students */}
            {commenRequiredDocs.length > 0 && (
              <div className="pp-form-section">
                {commenRequiredDocs.map((requiredDoc: any, index: number) => (
                  <div key={index} className="pp-form-field">
                    <label className="pp-form-label">{requiredDoc?.displayname}</label>
                    <input
                      type="file"
                      className="form-control"
                      name={`selected_required_document_${requiredDoc?.id}`}
                      onChange={(e: any) => {
                        let uploadedDoc = { id: requiredDoc.id, uploadedFile: e.target.files[0], iscommen: requiredDoc.commenfile, filetype: requiredDoc.filetype, "student_id": "" };
                        let tempuploadedDocs = [...uploadedDocs];
                        let foundIndex = tempuploadedDocs?.findIndex((doc: any) => doc.id == requiredDoc.id);
                        if (foundIndex == -1) {
                          tempuploadedDocs.push(uploadedDoc);
                        } else {
                          tempuploadedDocs[foundIndex].uploadedFile = e.target.files[0];
                        }
                        setUploadedDocs(tempuploadedDocs);
                      }}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Documents required per student */}
            <h3 className="pp-form-section-title">
              <i className="fas fa-user-graduate" aria-hidden="true"></i>
              {t.translate("Students required documents")}
            </h3>
            {studentsRequiredDocs.map((studentDocs: any, index: number) => {
              if (studentDocs?.required_docs?.filter((f: any) => f.commenfile != 1)?.length > 0) {
                return (
                  <div key={index} className="pp-form-section">
                    <div className="pp-form-question">
                      {t.translate("Name")} : {studentDocs?.student?.fullname}
                    </div>
                    {studentDocs?.required_docs?.filter((f: any) => f.commenfile != 1).map((requiredDoc: any, index: number) => (
                      <div key={index} className="pp-form-field">
                        <label className="pp-form-label">{requiredDoc?.displayname}</label>
                        <input
                          type="file"
                          className="form-control"
                          name={`required_document_${studentDocs?.student?.id}_${requiredDoc?.id}`}
                          onChange={(e: any) => {
                            let uploadedDoc = { id: `${studentDocs?.student?.id}_${requiredDoc?.id}`, uploadedFile: e.target.files[0], iscommen: requiredDoc.commenfile, filetype: requiredDoc.filetype, "student_id": studentDocs?.student?.id };
                            let tempuploadedDocs = [...uploadedDocs];
                            let foundIndex = tempuploadedDocs?.findIndex((doc: any) => doc.id == `${studentDocs?.student?.id}_${requiredDoc?.id}`);
                            if (foundIndex == -1) {
                              tempuploadedDocs.push(uploadedDoc);
                            } else {
                              tempuploadedDocs[foundIndex].uploadedFile = e.target.files[0];
                            }
                            setUploadedDocs(tempuploadedDocs);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                );
              }
              return null;
            })}

            <div className="pp-form-actions">
              <button type="submit" className="btn pp-btn-submit">
                {t.translate("Submit")}
              </button>
            </div>
          </Form>
        </ModalBody>
        <ModalFooter>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setRequiredDocsModal(false)}>
            {t.translate("Cancel")}
          </button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default Home;
